/**
 * Bot status primitives
 *
 * Shared by the super-admin catalogue (pages/AdminBots.tsx) and the user-facing
 * Bot Override page (pages/BotOverride.tsx) so the two can't drift on what
 * "healthy" looks like.
 *
 * Deliberately no avatar image: BotAccountResponse carries identity and status
 * only — no profile image URL — so fetching one would mean a Twitch call per
 * bot. The initial circle matches the account treatment in Layout.tsx.
 */

import type { BotAccount } from '@/api/botsApi'
import clsx from 'clsx'

/** Coarse health of a bot account, in the order the UI cares about. */
export type BotHealth = 'disabled' | 'expired' | 'healthy'

/**
 * Health of a bot account.
 *
 * `is_token_expired` is false when token_expires_at is unknown (the system bot
 * seeded from .env often has no expiry recorded), so "healthy" means "no known
 * problem" rather than "verified against Twitch".
 */
export function botHealth(bot: BotAccount): BotHealth {
  if (!bot.is_active) return 'disabled'
  if (bot.is_token_expired) return 'expired'
  return 'healthy'
}

/** Initial-circle stand-in for a bot's avatar. */
export function BotAvatar({
  name,
  size = 'md',
  tone = 'magenta',
}: {
  name: string
  size?: 'sm' | 'md' | 'lg'
  tone?: 'magenta' | 'cyan' | 'mint' | 'muted' | 'danger'
}) {
  const dims = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-11 h-11 text-base',
    lg: 'w-14 h-14 text-xl',
  }[size]

  const tones = {
    magenta: 'bg-synth-magenta/20 text-synth-magenta',
    cyan: 'bg-synth-cyan/20 text-synth-cyan',
    mint: 'bg-synth-mint/20 text-synth-mint',
    muted: 'bg-surface-overlay text-text-muted',
    danger: 'bg-state-danger-bg text-state-danger',
  }[tone]

  return (
    <div
      className={clsx(
        'rounded-full flex items-center justify-center shrink-0 font-medium',
        dims,
        tones,
      )}
      aria-hidden="true"
    >
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  )
}

/** Small pill. Exported so pages can add their own without redefining styles. */
export function Pill({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode
  tone?: 'magenta' | 'cyan' | 'mint' | 'warning' | 'danger' | 'muted'
}) {
  const tones = {
    magenta: 'bg-synth-magenta/15 text-synth-magenta',
    cyan: 'bg-synth-cyan/15 text-synth-cyan',
    mint: 'bg-synth-mint/15 text-synth-mint',
    warning: 'bg-state-warning/15 text-state-warning',
    danger: 'bg-state-danger-bg text-state-danger',
    muted: 'bg-surface-overlay text-text-muted',
  }[tone]

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs whitespace-nowrap',
        tones,
      )}
    >
      {children}
    </span>
  )
}

/** Token health, as a pill. */
export function BotTokenBadge({ bot }: { bot: BotAccount }) {
  const health = botHealth(bot)
  if (health === 'disabled') return <Pill tone="muted">Disabled</Pill>
  if (health === 'expired') return <Pill tone="danger">Token expired</Pill>
  return <Pill tone="mint">Healthy</Pill>
}

/** System bot vs a user-registered one. */
export function BotTypeBadge({ bot }: { bot: BotAccount }) {
  return bot.is_system_default ? (
    <Pill tone="cyan">System</Pill>
  ) : (
    <Pill tone="magenta">User</Pill>
  )
}
